// Batch I — Stale AI Analysis Banner.
//
// Detects when the deal's stored ai_summary references inputs/metrics
// that no longer match what's currently saved. Most common cause:
// user edited the deal (raised price, changed rent, adjusted down
// payment) AFTER running AI, so the verdict text quotes stale numbers
// while the headline KPI cards refresh live.
//
// Renders a thin yellow strip above the KPI grid with the specific
// fields that drifted + a "Re-run analysis" button.

import { Button } from "@/components/ui/button";
import { AlertCircle, RefreshCw, Loader2 } from "lucide-react";
import { useFmt } from "@/lib/useFmt";

// Fields we treat as "headline" — when these drift, the verdict text
// almost certainly references a number that no longer matches.
// counter_offer_price is included because the AI verdict prompt
// (ai_service._format_deal_context) explicitly quotes the counter
// inside the COUNTER-OFFER UNDER CONSIDERATION block, so editing
// the counter alone is enough to invalidate the frozen verdict.
//
// Keep this in lock-step with the inputs_snapshot keys written by
// server.ai_analyze() — any field listed here that isn't in the
// snapshot will always look "drifted" (snap=undefined vs current=X).
const TRACKED = [
  { key: "purchase_price",         label: "Purchase price",  kind: "money" },
  { key: "counter_offer_price",    label: "Counter offer",   kind: "money" },
  // Phase 36 — flipping buyer<->seller changes the verdict prompt
  // framing AND the price basis, so the stored ai_summary is no
  // longer telling the right story. Surface it as a drift.
  { key: "role",                   label: "Analysis role",   kind: "raw" },
  { key: "monthly_rent",           label: "Monthly rent",    kind: "money" },
  { key: "down_payment_percent",   label: "Down payment %",  kind: "percent_raw" },
  { key: "interest_rate",          label: "Interest rate",   kind: "percent_raw" },
  { key: "loan_term_years",        label: "Loan term",       kind: "raw" },
  { key: "closing_costs",          label: "Closing costs",   kind: "money" },
  { key: "repairs",                label: "Repairs",         kind: "money" },
  { key: "hoa_monthly",            label: "HOA / mo",        kind: "money" },
  { key: "taxes_annual",           label: "Taxes / yr",      kind: "money" },
  { key: "insurance_annual",       label: "Insurance / yr",  kind: "money" },
];

function almostEqual(a, b) {
  if (a === null || a === undefined) return b === null || b === undefined;
  if (b === null || b === undefined) return false;
  const an = Number(a), bn = Number(b);
  if (!Number.isFinite(an) || !Number.isFinite(bn)) return a === b;
  // 1 cent / 0.01 pp tolerance — anything bigger means a real user edit.
  return Math.abs(an - bn) < Math.max(0.01, Math.abs(an) * 0.0001);
}

export default function StaleAnalysisBanner({ deal, onRerun, running }) {
  const fmt = useFmt();
  const snap = deal?.ai_summary?.inputs_snapshot;
  if (!deal?.ai_summary || !snap) return null;

  const drifts = TRACKED
    .map((f) => {
      const before = snap[f.key];
      const after = deal[f.key];
      if (almostEqual(before, after)) return null;
      return { ...f, before, after };
    })
    .filter(Boolean);

  if (drifts.length === 0) return null;

  const fmtVal = (v, kind) => {
    if (v === null || v === undefined) return "—";
    if (kind === "money") return fmt.money(v, { primaryOnly: true });
    if (kind === "percent_raw") return `${Number(v).toFixed(2)}%`;
    return String(v);
  };

  // Headline summary — pick the top 2 drifts for the one-line summary
  // and tuck the rest behind "and N more".
  const head = drifts.slice(0, 2);
  const extra = drifts.length - head.length;

  return (
    <div
      data-testid="stale-analysis-banner"
      className="mb-6 flex flex-col gap-3 rounded-xl border border-amber-200 bg-amber-50/60 p-4 shadow-sm sm:flex-row sm:items-start"
    >
      <div className="flex flex-shrink-0 items-start gap-2 text-amber-700">
        <AlertCircle className="mt-0.5 h-4 w-4" />
        <div className="flex-1 text-sm">
          <p className="font-semibold">AI analysis is out of date.</p>
          <p className="mt-0.5 text-amber-800/80">
            The verdict was generated when{" "}
            {head.map((d, i) => (
              <span key={d.key}>
                <span className="font-medium">{d.label}</span>{" "}
                was {fmtVal(d.before, d.kind)}{" "}
                (now {fmtVal(d.after, d.kind)})
                {i < head.length - 1 ? ", and " : ""}
              </span>
            ))}
            {extra > 0 ? <> and {extra} other {extra === 1 ? "field" : "fields"} changed</> : ""}.
            Re-run for a refreshed verdict.
          </p>
        </div>
      </div>
      <div className="sm:ml-auto">
        <Button
          data-testid="stale-analysis-rerun"
          size="sm"
          onClick={onRerun}
          disabled={running}
          className="bg-amber-600 hover:bg-amber-700"
        >
          {running ? (
            <>
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> Re-running
            </>
          ) : (
            <>
              <RefreshCw className="mr-2 h-3.5 w-3.5" /> Re-run analysis
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
